// Per-client detail — read-mode summary by default, "Edit" toggles to
// inline form, separate sections for API key + decision charts +
// yield diagnostics.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import ChartBar from "../components/ChartBar";
import ChartHistogram, {
  type HistogramBin,
} from "../components/ChartHistogram";
import ChartLine from "../components/ChartLine";
import {
  api,
  type ClientDetailResponse,
  type ClientPatchRequest,
  type ClientStatsResponse,
  type ClientYieldResponse,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  FieldLabel,
  inputStyle,
  PageHeader,
  Pill,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

const SESSION_KEY_TTL_MS = 30 * 60 * 1000;

const REASON_LABELS: Record<string, string> = {
  fresh_pull_full_price: "Accepted (full)",
  yield_throttled: "Accepted (partial throttle)",
  cache_hit: "Accepted (cached)",
  below_threshold_cached: "Below threshold (cached)",
  below_threshold_fresh: "Below threshold",
  no_match_upstream: "No match",
  upstream_timeout: "Upstream timeout",
  upstream_error: "Upstream error",
  upstream_invalid_input: "Invalid input",
  validation_failed: "Validation failed",
  rate_limited: "Rate limited",
  unauthorized: "Unauthorized",
  idempotent_replay: "Idempotent replay",
};

function fmtMs(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return `${n.toFixed(0)} ms`;
}

export default function ClientDetail() {
  const params = useParams();
  const id = params["id"] ?? "";
  const sessionKey = `loop-rtb:apikey:${id}`;

  const [client, setClient] = useState<ClientDetailResponse | null>(null);
  const [stats, setStats] = useState<ClientStatsResponse | null>(null);
  const [yieldData, setYieldData] = useState<ClientYieldResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Plaintext key — surfaced after rotate, persisted in sessionStorage.
  const [rotatedKeyPlaintext, setRotatedKeyState] = useState<string | null>(
    () => readSessionKey(sessionKey),
  );
  const setRotatedKeyPlaintext = (val: string | null): void => {
    setRotatedKeyState(val);
    try {
      if (val === null) sessionStorage.removeItem(sessionKey);
      else
        sessionStorage.setItem(
          sessionKey,
          JSON.stringify({ value: val, ts: Date.now() }),
        );
    } catch {
      /* sessionStorage may be unavailable in private mode */
    }
  };
  const [rotating, setRotating] = useState(false);
  const [confirmingRotate, setConfirmingRotate] = useState(false);
  const [copied, setCopied] = useState(false);

  // Configuration view-vs-edit mode.
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ClientPatchRequest>({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      api.getClient(id),
      api.clientStats(id),
      api.clientYield(id),
    ]).then((results) => {
      if (cancelled) return;
      const [c, s, y] = results;
      if (c.status === "fulfilled") {
        setClient(c.value);
        setForm({
          active: c.value.active,
          full_price: c.value.full_price,
          partial_price: c.value.partial_price,
          partial_match_ratio: c.value.partial_match_ratio,
          rps_cap: c.value.rps_cap,
        });
      } else {
        setError(
          c.reason instanceof Error ? c.reason.message : "Failed to load",
        );
      }
      if (s.status === "fulfilled") setStats(s.value);
      if (y.status === "fulfilled") setYieldData(y.value);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const onRotate = async (): Promise<void> => {
    if (!id) return;
    setRotating(true);
    setError(null);
    try {
      const res = await api.rotateKey(id);
      setRotatedKeyPlaintext(res.api_key_plaintext);
      setConfirmingRotate(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Generate failed");
    } finally {
      setRotating(false);
    }
  };

  const onCopyKey = async (): Promise<void> => {
    if (!rotatedKeyPlaintext) return;
    try {
      await navigator.clipboard.writeText(rotatedKeyPlaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard not permitted */
    }
  };

  const onSave = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const updated = await api.patchClient(id, form);
      setClient((prev) => (prev ? { ...prev, ...updated } : prev));
      setSaveMessage("Saved.");
      setEditing(false);
      setTimeout(() => setSaveMessage(null), 2400);
    } catch (err) {
      setSaveMessage(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onCancelEdit = (): void => {
    if (!client) return;
    setForm({
      active: client.active,
      full_price: client.full_price,
      partial_price: client.partial_price,
      partial_match_ratio: client.partial_match_ratio,
      rps_cap: client.rps_cap,
    });
    setEditing(false);
    setSaveMessage(null);
  };

  if (loading && !client) {
    return (
      <div>
        <PageHeader title={<Skeleton width={220} height={26} />} />
        <Skeleton height={140} />
      </div>
    );
  }

  if (error && !client) {
    return (
      <Card>
        <EmptyState title="Failed to load client" hint={error} />
      </Card>
    );
  }

  if (!client) {
    return (
      <Card>
        <EmptyState title="Client not found" />
      </Card>
    );
  }

  const decisionData = (client.stats7d?.decisionReasonCounts ?? []).map(
    (r) => ({ x: REASON_LABELS[r.reason] ?? r.reason, count: r.count }),
  );
  const acceptMix = (stats?.acceptMixHourly ?? []).map((p) => ({
    x: p.ts,
    full: p.full,
    partial: p.partial,
  }));
  const streakBins: HistogramBin[] = (yieldData?.streakDistribution ?? []).map(
    (s) => ({ label: REASON_LABELS[s.status] ?? s.status, count: s.count }),
  );

  return (
    <div>
      <PageHeader
        title={client.client_name}
        subtitle={
          <span style={{ fontFamily: tokens.font.mono }}>
            {client.client_id}
          </span>
        }
        actions={
          <Pill tone={client.active ? "success" : "neutral"}>
            {client.active ? "Active" : "Disabled"}
          </Pill>
        }
      />

      {/* ───────────── Configuration ───────────── */}
      <Card
        title="Configuration"
        action={
          editing ? null : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              style={buttonStyle("ghost")}
            >
              Edit
            </button>
          )
        }
        style={{ marginBottom: tokens.space.xl }}
      >
        {editing ? (
          <form onSubmit={onSave}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: tokens.space.lg,
              }}
            >
              <div>
                <FieldLabel htmlFor="cfg-active">Active</FieldLabel>
                <label
                  style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                  <input
                    id="cfg-active"
                    type="checkbox"
                    checked={form.active ?? false}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, active: e.target.checked }))
                    }
                  />
                  <span style={{ color: tokens.color.fg1 }}>
                    {form.active ? "Accepting traffic" : "Paused"}
                  </span>
                </label>
              </div>
              <div>
                <FieldLabel htmlFor="cfg-full-price">Full price (USD)</FieldLabel>
                <input
                  id="cfg-full-price"
                  inputMode="decimal"
                  value={form.full_price ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, full_price: e.target.value }))
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <FieldLabel htmlFor="cfg-partial-price">
                  Partial price (USD)
                </FieldLabel>
                <input
                  id="cfg-partial-price"
                  inputMode="decimal"
                  value={form.partial_price ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, partial_price: e.target.value }))
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <FieldLabel htmlFor="cfg-ratio">
                  Partial match ratio (0–1)
                </FieldLabel>
                <input
                  id="cfg-ratio"
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={form.partial_match_ratio ?? 0}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      partial_match_ratio: Number(e.target.value),
                    }))
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <FieldLabel htmlFor="cfg-rps">RPS cap</FieldLabel>
                <input
                  id="cfg-rps"
                  type="number"
                  min="1"
                  max="500"
                  value={form.rps_cap ?? 0}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, rps_cap: Number(e.target.value) }))
                  }
                  style={inputStyle}
                />
              </div>
            </div>
            <div
              style={{
                marginTop: tokens.space.lg,
                display: "flex",
                gap: tokens.space.sm,
                alignItems: "center",
              }}
            >
              <button type="submit" disabled={saving} style={buttonStyle("primary", saving)}>
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button
                type="button"
                onClick={onCancelEdit}
                disabled={saving}
                style={buttonStyle("ghost", saving)}
              >
                Cancel
              </button>
              {saveMessage && (
                <span style={{ color: tokens.color.fg1, fontSize: tokens.font.size.sm }}>
                  {saveMessage}
                </span>
              )}
            </div>
          </form>
        ) : (
          <ReadOnlyConfig client={client} />
        )}
      </Card>

      {/* ───────────── API key ───────────── */}
      <Card title="API key" style={{ marginBottom: tokens.space.xl }}>
        <p
          style={{
            color: tokens.color.fg1,
            margin: `0 0 ${tokens.space.md}px`,
            fontSize: tokens.font.size.base,
          }}
        >
          Only an HMAC of each key is stored — the plaintext can't be recovered.
          Generate a new key when you need a usable value; the previous key
          stops working immediately.
        </p>

        {rotatedKeyPlaintext ? (
          <div style={apiKeyCard}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: tokens.space.sm,
              }}
            >
              <span
                style={{
                  fontSize: tokens.font.size.xs,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: tokens.color.tealLight,
                  fontWeight: 600,
                }}
              >
                Active plaintext
              </span>
              <span
                style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}
              >
                Cleared on tab close
              </span>
            </div>
            <pre style={keyPre}>
              <code>{rotatedKeyPlaintext}</code>
            </pre>
            <div
              style={{
                marginTop: tokens.space.sm,
                display: "flex",
                gap: tokens.space.sm,
              }}
            >
              <button
                type="button"
                onClick={() => void onCopyKey()}
                style={buttonStyle("subtle")}
              >
                {copied ? "Copied ✓" : "Copy"}
              </button>
              <button
                type="button"
                onClick={() => setRotatedKeyPlaintext(null)}
                style={buttonStyle("ghost")}
              >
                Hide
              </button>
            </div>
          </div>
        ) : null}

        {confirmingRotate ? (
          <div style={confirmCard}>
            <div style={{ marginBottom: tokens.space.md }}>
              <strong style={{ color: tokens.color.fg0 }}>
                Generate a new API key?
              </strong>
              <div
                style={{
                  color: tokens.color.fg1,
                  marginTop: tokens.space.xs,
                  fontSize: tokens.font.size.sm,
                }}
              >
                The current key stops working immediately. Share the new key
                with the integration partner.
              </div>
            </div>
            <div style={{ display: "flex", gap: tokens.space.sm, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => void onRotate()}
                disabled={rotating}
                style={buttonStyle("primary", rotating)}
              >
                {rotating ? "Generating…" : "Yes, generate new key"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingRotate(false)}
                disabled={rotating}
                style={buttonStyle("ghost", rotating)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingRotate(true)}
            disabled={rotating}
            style={buttonStyle("primary", rotating)}
          >
            {rotatedKeyPlaintext ? "Generate another key" : "Generate new key"}
          </button>
        )}
      </Card>

      {/* ───────────── Last 7 days ───────────── */}
      <Card title="Last 7 days" style={{ marginBottom: tokens.space.xl }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: tokens.space.lg,
            marginBottom: tokens.space.lg,
          }}
        >
          <StatCard
            label="P50 latency"
            value={fmtMs(client.stats7d?.latencyMs.p50)}
          />
          <StatCard
            label="P95 latency"
            value={fmtMs(client.stats7d?.latencyMs.p95)}
          />
          <StatCard
            label="P99 latency"
            value={fmtMs(client.stats7d?.latencyMs.p99)}
          />
        </div>
        <h3
          style={{
            margin: `0 0 ${tokens.space.sm}px`,
            color: tokens.color.fg1,
            fontSize: tokens.font.size.sm,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          Decision breakdown
        </h3>
        {decisionData.length > 0 ? (
          <ChartBar
            data={decisionData}
            bars={[{ key: "count", name: "Requests" }]}
            layout="horizontal"
            height={Math.max(200, decisionData.length * 36 + 40)}
            showValues
          />
        ) : (
          <EmptyState
            title="No decisions yet"
            hint="Once this client sends traffic, the breakdown will populate here."
          />
        )}
      </Card>

      {/* ───────────── Conversion ───────────── */}
      <Card title="Conversion" style={{ marginBottom: tokens.space.xl }}>
        <Section title="Match rate over time">
          {(stats?.matchRateHourly ?? []).length > 0 ? (
            <ChartLine
              series={[
                {
                  key: "match_rate",
                  name: "Match rate",
                  data: stats?.matchRateHourly ?? [],
                },
              ]}
              format={(n) => `${(n * 100).toFixed(0)}%`}
              yDomain={[0, 1]}
            />
          ) : (
            <EmptyChart label="match-rate trend" />
          )}
        </Section>
        <Section title="Accept mix (full vs partial)">
          {acceptMix.length > 0 ? (
            <ChartBar
              data={acceptMix}
              stacked
              bars={[
                { key: "full", name: "Full", color: tokens.color.tealLight },
                { key: "partial", name: "Partial", color: tokens.color.warning },
              ]}
            />
          ) : (
            <EmptyChart label="accept-mix history" />
          )}
        </Section>
        <Section title="Cache hit rate">
          {(stats?.cacheHitRateHourly ?? []).length > 0 ? (
            <ChartLine
              series={[
                {
                  key: "cache_hit_rate",
                  name: "Cache hit rate",
                  data: stats?.cacheHitRateHourly ?? [],
                },
              ]}
              format={(n) => `${(n * 100).toFixed(0)}%`}
              yDomain={[0, 1]}
            />
          ) : (
            <EmptyChart label="cache-hit history" />
          )}
        </Section>
      </Card>

      {/* ───────────── Yield diagnostics ───────────── */}
      <Card title="Yield diagnostics">
        <Section title="Configured vs observed partial rate">
          {(yieldData?.configuredVsObserved ?? []).length > 0 ? (
            <ChartLine
              series={[
                {
                  key: "observed",
                  name: "Observed",
                  color: tokens.color.tealLight,
                  data: yieldData?.configuredVsObserved ?? [],
                },
                {
                  key: "configured",
                  name: "Configured",
                  color: tokens.color.warning,
                  data: (yieldData?.configuredVsObserved ?? []).map((p) => ({
                    ts: p.ts,
                    value: client.partial_match_ratio,
                  })),
                },
              ]}
              format={(n) => `${(n * 100).toFixed(0)}%`}
              yDomain={[0, 1]}
            />
          ) : (
            <EmptyChart label="yield convergence" />
          )}
        </Section>
        <Section title="Streak distribution">
          {streakBins.length > 0 ? (
            <ChartHistogram bins={streakBins} />
          ) : (
            <EmptyChart label="streak distribution" />
          )}
        </Section>
        <Section title="Hourly variance">
          {(yieldData?.hourlyVariance ?? []).length > 0 ? (
            <ChartLine
              series={[
                {
                  key: "variance",
                  name: "Partial rate",
                  data: yieldData?.hourlyVariance ?? [],
                },
              ]}
              format={(n) => `${(n * 100).toFixed(0)}%`}
              yDomain={[0, 1]}
            />
          ) : (
            <EmptyChart label="hourly variance" />
          )}
        </Section>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only configuration (default state)
// ---------------------------------------------------------------------------

function ReadOnlyConfig({ client }: { client: ClientDetailResponse }) {
  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: tokens.space.lg,
        margin: 0,
      }}
    >
      <ReadField label="Status">
        <Pill tone={client.active ? "success" : "neutral"}>
          {client.active ? "Accepting traffic" : "Paused"}
        </Pill>
      </ReadField>
      <ReadField label="Full price" mono>
        ${client.full_price}
      </ReadField>
      <ReadField label="Partial price" mono>
        ${client.partial_price}
      </ReadField>
      <ReadField label="Match ratio" mono>
        {client.partial_match_ratio.toFixed(2)}
      </ReadField>
      <ReadField label="RPS cap" mono>
        {client.rps_cap}
      </ReadField>
    </dl>
  );
}

function ReadField({
  label,
  mono = false,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt
        style={{
          fontSize: tokens.font.size.xs,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.color.fg1,
          fontWeight: 600,
          marginBottom: tokens.space.xs,
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          color: tokens.color.fg0,
          fontSize: tokens.font.size.lg,
          fontWeight: 600,
          ...(mono
            ? {
                fontFamily: tokens.font.mono,
                fontVariantNumeric: "tabular-nums",
              }
            : {}),
        }}
      >
        {children}
      </dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: tokens.space.lg }}>
      <h3
        style={{
          margin: `0 0 ${tokens.space.sm}px`,
          color: tokens.color.fg1,
          fontSize: tokens.font.size.sm,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

function EmptyChart({ label }: { label: string }) {
  return (
    <div
      style={{
        background: tokens.color.bg1,
        border: `1px dashed ${tokens.color.borderMid}`,
        borderRadius: tokens.radius.md,
        padding: tokens.space.xxl,
        textAlign: "center",
        color: tokens.color.fg2,
        fontSize: tokens.font.size.sm,
      }}
    >
      No data for {label} yet.
    </div>
  );
}

function readSessionKey(storageKey: string): string | null {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { value?: string; ts?: number };
    if (typeof parsed.value !== "string" || typeof parsed.ts !== "number")
      return null;
    if (Date.now() - parsed.ts > SESSION_KEY_TTL_MS) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}

const apiKeyCard: React.CSSProperties = {
  marginBottom: tokens.space.md,
  padding: tokens.space.lg,
  background: tokens.color.bg1,
  border: "1px solid rgba(0,212,170,0.30)",
  borderRadius: tokens.radius.md,
  boxShadow: "0 0 0 1px rgba(0,212,170,0.06)",
};

const confirmCard: React.CSSProperties = {
  marginBottom: tokens.space.md,
  padding: tokens.space.lg,
  background: tokens.color.bg3,
  border: `1px solid ${tokens.color.border}`,
  borderRadius: tokens.radius.md,
};

const keyPre: React.CSSProperties = {
  margin: 0,
  padding: "10px 12px",
  background: tokens.color.bg0,
  color: tokens.color.tealLight,
  borderRadius: tokens.radius.sm,
  fontFamily: tokens.font.mono,
  fontSize: tokens.font.size.sm,
  overflow: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-all",
  userSelect: "all",
};
